import { useState, useCallback, useEffect } from 'react';
import CustomSelect from '../../common/CustomSelect';
import CopyButton from '../../common/CopyButton';
import qrcode from 'qrcode';
import { useSelector } from 'react-redux';
import { useJunoCommonFunctions } from '../../../../client/Juno/helpers';
import { apiService } from '../../../../client/common/apiCallService';
import JunoButton from '../../common/JunoButton';
import { truncateMiddle } from '../../../helpers/constant';
import useWindowWidth from '../../../hooks/useWindowWidth';

const WARNING_CRYPTOS = ['DAI', 'USDT', 'USDC', 'TRC20-USDT'];

// This component can be used directly in the dashboard or inside a modal
const ReceiveCryptoForm = ({ isModal = false, open, row: selectedCurrency }) => {
    // Form state
    const {
        balancesCrypto = [],
        unfreezeCryptoCurrencies,
        clientData,
    } = useSelector(state => state.juno);
    const [selectedCrypto, setSelectedCrypto] = useState(null);
    const [selectedCryptoAddress, setSelectedCryptoAddress] = useState(null);
    const [imageData, setImageData] = useState(null);
    const [warning, setWarning] = useState(false);
    const [loading, setLoading] = useState(false);
    const [addressLoading, setAddressLoading] = useState(false);
    const [failedMsg, setFailedMsg] = useState('');
    const apiUrl = useSelector(state => state.config.api_url);
    const imageBaseUrl = useSelector(state => state.config.image_base_url);
    const { getWalletAddress } = useJunoCommonFunctions();
    const windowWidth = useWindowWidth();

    const MAX_RECIEVE_ADDRESS_SIZE = windowWidth < 1250 ? 12 : windowWidth < 1400 ? 17 : 22;

    useEffect(() => {
        if (!selectedCrypto && unfreezeCryptoCurrencies?.length > 0) {
            setSelectedCrypto(selectedCurrency ? selectedCurrency : unfreezeCryptoCurrencies[0]);
        }
    }, [unfreezeCryptoCurrencies, selectedCrypto]);

    const handleCryptoChange = useCallback(crypto => {
        setSelectedCrypto(crypto);
        setSelectedCryptoAddress(null);
        setImageData(null);
    }, []);

    useEffect(() => {
        if (selectedCrypto) {
            setWarning(WARNING_CRYPTOS.includes(selectedCrypto.currencyShortName));
        }
    }, [selectedCrypto]);

    // When the modal is opened from another component, fetch the address
    useEffect(() => {
        if (isModal && open && selectedCrypto) {
            fetchCryptoAddress();
        }
    }, [isModal, open, selectedCrypto]);

    const generateQRcode = useCallback(async address => {
        if (!address?.key) return null;

        try {
            const imgData = await new Promise((resolve, reject) => {
                qrcode.toDataURL(address.key, (err, data) => {
                    if (err) reject(err);
                    else resolve(data);
                });
            });
            setImageData(imgData);
        } catch (error) {
            console.error('Error generating QR code:', error);
            setFailedMsg('Failed to generate QR code. Please try again.');
        }
    }, []);

    const fetchCryptoAddress = useCallback(async () => {
        if (!selectedCrypto || !clientData?.accountNumber || !balancesCrypto?.length) return;

        setAddressLoading(true);

        try {
            const currentCrypto = balancesCrypto.find(
                crypto => crypto.currencyShortName === selectedCrypto.currencyShortName,
            );
            const currentBlockchain = currentCrypto?.blockchain;

            if (!currentBlockchain) return;

            const payload = {
                blockchainList: [currentBlockchain],
                accountNumber: clientData.accountNumber,
            };

            const response = await getWalletAddress(payload);
            const addressData = response?.data?.[0]?.address;

            if (addressData?.key) {
                setSelectedCryptoAddress(addressData);
                await generateQRcode(addressData);
            } else {
                setSelectedCryptoAddress(null);
                setImageData(null);
            }
        } catch (error) {
            console.error('Error fetching wallet address:', error);
            setFailedMsg(`Failed to fetch wallet address: ${error.message || 'Unknown error'}`);
            setSelectedCryptoAddress(null);
            setImageData(null);
        } finally {
            setAddressLoading(false);
        }
    }, [selectedCrypto, clientData?.accountNumber, balancesCrypto, generateQRcode]);

    useEffect(() => {
        if (!isModal && selectedCrypto) {
            fetchCryptoAddress();
        }
    }, [isModal, selectedCrypto, fetchCryptoAddress]);

    const generateCryptoAddress = useCallback(() => {
        if (!selectedCrypto?.blockchain || !clientData?.accountNumber) return;

        setLoading(true);
        const endpoint =
            (window.location.hostname === 'localhost' ? apiUrl : '') +
            '/restapi/generate-wallet-address';

        apiService(
            endpoint,
            {
                blockchain: selectedCrypto.blockchain,
                accountNumber: clientData.accountNumber,
            },
            async data => {
                setLoading(false);
                if (data) {
                    await fetchCryptoAddress();
                }
            },
            err => {
                setLoading(false);
                console.error('Error generating wallet address:', err);
                setFailedMsg('Failed to generate wallet address. Please try again.');
            },
        );
    }, [selectedCrypto, clientData?.accountNumber, fetchCryptoAddress]);

    // Content to render (used in both modal and dashboard)
    const renderContent = () => (
        <>
            <div className="flex flex-col gap-6 h-full w-full">
                {/* QR & Info Section */}
                <div className={`flex items-center flex-col lg:flex-row gap-4 lg:gap-0`}>
                    <div
                        className={`${!warning && 'flex justify-center items-center gap-4'} w-full flex-col`}>
                        <img
                            src={imageData || `${imageBaseUrl}/qr-code-sample.png`}
                            alt="QR-code"
                            className={`${!imageData && 'opacity-30 invert dark:invert-0'} w-36 h-36 object-contain`}
                        />
                        {!warning && (
                            <p className="!text-text text-base font-semibold uppercase text-center">
                                Only send {selectedCrypto?.currencyShortName} to this address
                            </p>
                        )}
                    </div>
                    <div className="flex flex-col">
                        {warning && (
                            <p className="!text-text text-base font-semibold uppercase">
                                Only send{' '}
                                {selectedCrypto?.currencyShortName == 'TRC20-USDT'
                                    ? 'USDT'
                                    : selectedCrypto?.currencyShortName}{' '}
                                ({selectedCrypto?.currencyShortName == 'TRC20-USDT' ? 'TRC' : 'ERC'}
                                -20) to this address
                            </p>
                        )}
                        {warning && (
                            <>
                                <p className="text-sm font-normal !text-text pt-1">
                                    Sending any other coin or token — including{' '}
                                    {selectedCrypto?.currencyShortName} on{' '}
                                    {selectedCrypto?.currencyShortName == 'USDC'
                                        ? 'another'
                                        : selectedCrypto?.currencyShortName == 'USDT'
                                          ? 'other'
                                          : 'a different'}{' '}
                                    networks (e.g.,{' '}
                                    {selectedCrypto?.currencyShortName != 'TRC20-USDT'
                                        ? 'TRC'
                                        : 'ERC'}
                                    -20 or Solana) — may result in the permanent loss of your
                                    deposit.
                                </p>
                            </>
                        )}
                    </div>
                </div>

                {/* Recipient Wallet Selector */}
                <CustomSelect
                    label="Recipient wallet"
                    options={unfreezeCryptoCurrencies}
                    onChange={handleCryptoChange}
                    value={selectedCrypto}
                />

                {/* Address Block */}
                <div className="p-3.5 border rounded-lg border-[#D1D1D6] dark:!border-[#51525C] w-full">
                    {addressLoading ? (
                        <div className="w-full flex flex-col gap-2">
                            <div className="animate-pulse h-4 bg-gray-100 dark:bg-gray-600 rounded w-40" />
                            <div className="animate-pulse h-12 bg-gray-100 dark:bg-gray-700 rounded w-full" />
                        </div>
                    ) : (
                        <div className="w-full">
                            <div className="flex flex-col justify-center items-start gap-2">
                                <div className="text-balanceCard-manage text-xs font-medium uppercase">
                                    {selectedCryptoAddress
                                        ? 'Generated receiving address'
                                        : 'Receiving address'}
                                </div>

                                {selectedCryptoAddress ? (
                                    <div className="text-xs w-full font-medium text-[#D1D1D6] flex items-center gap-2">
                                        <p className="w-4/5 !text-text text-sm font-medium shrink-0 mr-2">
                                            {selectedCryptoAddress.key?.length >
                                            MAX_RECIEVE_ADDRESS_SIZE
                                                ? truncateMiddle(
                                                      selectedCryptoAddress.key,
                                                      MAX_RECIEVE_ADDRESS_SIZE,
                                                  )
                                                : selectedCryptoAddress.key}
                                        </p>
                                        <div className="w-6 h-6 relative overflow-hidden">
                                            <CopyButton copyValue={selectedCryptoAddress.key} />
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        className={`text-center !text-error text-sm font-semibold uppercase ${isModal && 'mb-6'}`}>
                                        Not generated
                                    </div>
                                )}
                            </div>

                            {!selectedCryptoAddress && isModal && (
                                <div
                                    className="w-full self-stretch h-11 px-8 py-3 bg-white rounded-lg inline-flex justify-center items-center gap-2.5 overflow-hidden cursor-pointer"
                                    onClick={generateCryptoAddress}>
                                    <button className="text-center justify-start text-zinc-900 text-sm font-semibold cursor-pointer uppercase flex gap-2">
                                        {loading ? 'PROCESSING' : 'GENERATE'}
                                        {loading && <div className="jncButton__loader"></div>}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );

    // For modal mode, just return the content to be placed inside the parent's modal
    if (isModal) {
        return renderContent();
    }

    // For dashboard mode, render the full component
    return (
        <div className="h-full">
            {/* Dashboard View */}
            <div className="w-full h-auto !p-4 bg-modal-bg rounded-lg border border-[#E4E4E7] dark:!border-0 bg-quickforms-bg mb-2">
                {renderContent()}
            </div>

            {/* Generate Button for Dashboard */}
            {!isModal && !selectedCryptoAddress && !addressLoading && (
                <JunoButton type="widget" onClick={generateCryptoAddress} loading={loading}>
                    Generate
                </JunoButton>
            )}
        </div>
    );
};

export default ReceiveCryptoForm;
